import { useFragment } from '$gql';
import noop from 'lodash/noop';
import type { Readable, Writable } from 'svelte/store';
import { derived, get, writable } from 'svelte/store';
import type { WrapData } from '$lib/apollo/ApolloData';
import {
    createQueryInCacheAndObserve,
    type QueryInCacheAndObserveReturn,
} from '$lib/apollo/SvelteApollo';
import type { ShareableContainer } from '$lib/container/shareableContainer';
import singleton from '$lib/utils/factory';
import type { lazy } from '$lib/utils/types';
import {
    type CurrentUser_MeFragment,
    CurrentUser_MeFragmentDoc,
    CurrentUser_MeQueryDocument,
    type CurrentUser_MeQueryQuery,
} from '$gql/graphql';

export class CurrentUserStore {
    private readonly currentUserQuery: Writable<QueryInCacheAndObserveReturn<CurrentUser_MeQueryQuery> | null>;
    private readonly closeCallbacks: (() => void)[] = [];

    constructor(
        private readonly initialId: number | null,
        private readonly initialData: WrapData<CurrentUser_MeQueryQuery> | null,
        private readonly deps: Pick<ShareableContainer, 'apollo' | 'browser'>,
    ) {
        this.currentUserQuery = writable(
            this.initialData == null
                ? null
                : createQueryInCacheAndObserve(this.initialData, {}, this.deps),
        );

        if (this.initialData == null) {
            const watchQuery = this.deps.apollo().watchQuery({
                query: CurrentUser_MeQueryDocument,
                variables: {},
                returnPartialData: false,
            });

            const subscription = watchQuery.subscribe((result) => {
                this.setData({
                    query: CurrentUser_MeQueryDocument,
                    data: result.data,
                    variables: {},
                    type: 'ApolloData_WrapData',
                });
            });

            this.closeCallbacks.push(() => {
                subscription.unsubscribe();
            });
        }
    }

    close() {
        this.closeCallbacks.forEach((cb) => {
            cb();
        });
        this.closeCallbacks.splice(0, this.closeCallbacks.length); // clear array in place
    }

    setData(data: WrapData<CurrentUser_MeQueryQuery> | null) {
        this.currentUserQuery.update((query) => {
            if (query === null) {
                if (data != null) {
                    return createQueryInCacheAndObserve(data, {}, this.deps);
                }

                return null;
            } else {
                if (data != null) {
                    query.setOptions(data);
                    return query;
                }

                return null;
            }
        });
    }

    currentUser: lazy<Readable<CurrentUser_MeFragment | null>> = singleton(() => {
        const $query = get(this.currentUserQuery);
        const initialData =
            $query == null
                ? null
                : (useFragment(CurrentUser_MeFragmentDoc, get($query.data).me) ?? null);

        return derived(
            this.currentUserQuery,
            ($query, set) => {
                let unsubscriber: () => void;

                if ($query == null) {
                    set(null);
                    unsubscriber = noop;
                } else {
                    unsubscriber = $query.data.subscribe(($data) => {
                        set(useFragment(CurrentUser_MeFragmentDoc, $data.me) ?? null);
                    });
                }

                return () => {
                    unsubscriber();
                };
            },
            initialData,
        );
    });

    currentUserId(): Readable<number | null> {
        return derived(this.currentUser(), ($user, set) => {
            set($user?.id ?? this.initialId ?? null);
        });
    }
}
